import type { Messages } from "../fr";

const elapsed = (minutes: number) => (minutes < 1 ? "just now" : `${minutes} min`);

const forOffer = (offer: string | null) => (offer ? ` for ${offer}` : "");

export const shell: Messages["shell"] = {
  nav: {
    label: "Main navigation",
    home: "Hune — overview",
    overview: "Overview",
    leads: "Leads",
    reports: "Passes",
    offers: "My offers",
    settings: "Settings",
    admin: "Admin",
    signOut: "Log out",
  },
  workspace: {
    readOnly: "Read only",
    label: "Workspace viewed",
    mine: "My workspace",
  },
  topbar: {
    unavailable: "Offers and account unavailable.",
    account: "My account",
  },
  plan: {
    names: { free: "Free", solo: "Solo", pro: "Pro" },
    current: (name: string) => `${name} plan`,
    upgrade: "Upgrade to Solo",
    upgradeShort: "Plans",
  },
  offerSwitcher: {
    label: "Offer viewed",
    placeholder: "Choose an offer",
    add: "Add an offer",
  },
  runIndicator: {
    completed: "Pass complete",
    failed: "Pass interrupted",
    stalled: "No news from the pass",
    radarSearching: "Monitoring is searching",
    agentSearching: "The agent is searching",
    seeOutcome: "See the summary",
    seeReason: "See why",
    elapsed,
    noSignSince: (minutes: number) => (minutes < 1 ? "No sign of activity yet" : `No sign of activity for ${minutes} min`),
    background: "The agent works on our servers: you can browse elsewhere or close the tab, the pass keeps going.",
  },
  passNotification: {
    interrupted: (offer: string | null) => `Pass interrupted${forOffer(offer)}`,
    interruptedBody: "Open Hune to see why and run it again.",
    completed: (offer: string | null) => `Pass complete${forOffer(offer)}`,
    nothingNewBody: "Nothing new this time: the signals were about organizations you already track.",
    newLeads: (count: number, offer: string | null) => `${count} new ${count === 1 ? "lead" : "leads"}${forOffer(offer)}`,
    reactivated: (count: number, offer: string | null) =>
      `${count} ${count === 1 ? "lead" : "leads"} reactivated${forOffer(offer)}`,
    names: (names: string, rest: number) => (rest > 0 ? `${names} and ${rest} more.` : `${names}.`),
    alsoReactivated: (count: number) => `${count} ${count === 1 ? "lead" : "leads"} reactivated.`,
  },
  palette: {
    search: "Search",
    title: "Search Hune",
    description: "Go to a lead, a contact, a pass or a screen",
    placeholder: "A lead, a contact, an action…",
    empty: "No matches.",
    leads: "Leads",
    recentLeads: "Recent leads",
    contacts: "Contacts",
    passes: "Passes",
    passReport: "Pass report",
    actions: "Actions",
    launchPass: "Run a pass",
    launchKeywords: "analysis monitoring search",
    switchOffer: (name: string) => `Switch to offer ${name}`,
    offerKeyword: "offer",
    addOffer: "Add an offer",
    exportLeads: "Export leads (CSV)",
    exportKeywords: "csv excel sheets download",
    exportFailed: "The export failed.",
    goTo: "Go to",
    goToPage: (page: string) => `Go to ${page}`,
  },
  leadStatuses: {
    new: "New",
    to_contact: "To contact",
    contacted: "Contacted",
    replied: "Replied",
    won: "Won",
    dismissed: "Dismissed",
  },
  tour: {
    welcome: {
      running: {
        title: "Your first analysis has started",
        body: "While the agent searches, here’s how Hune works.",
        note: "You can close this: the analysis keeps going.",
      },
      settled: {
        title: "Your first analysis is done",
        body: "Its summary is waiting for you on this page. Before you open it, here’s how Hune works.",
        note: "The tour takes about a minute.",
      },
    },
    steps: {
      passage: {
        title: { running: "The agent is exploring your market", settled: "The summary of your first pass" },
        body: {
          running:
            "These steps follow its actual work: searches run, pages read, signals kept. When it’s done, this card shows the summary: new leads, reactivated, already known.",
          settled:
            "Every pass is summed up here: new leads, reactivated, already known. The full report and the source of each signal stay in “Passes”.",
        },
      },
      leads: {
        title: "Your leads land here",
        body: "Each lead: an organization, a dated and sourced signal, the right contacts. Hune never contacts anyone: you move each lead forward.",
      },
      ciblage: {
        title: "You steer the agent",
        body: "The agent infers your target from your website: correct it in “My offers”, your version is the one it uses. Dismiss a lead with a reason and it will take it into account on the next pass.",
      },
      veille: {
        title: "Then monitoring takes over",
        body: {
          running:
            "After this first pass, the agent will check your market again on its own and only flag what’s new. The current pass keeps going even if you close the tab.",
          settled:
            "The agent checks your market again on its own and only flags what’s new: nothing to rerun. This switch pauses monitoring.",
        },
      },
    },
    lastAction: { running: "Back to the pass", settled: "See the summary" },
    counter: (step: number, total: number) => `${step} of ${total}`,
    next: "Next",
    skip: "Skip the tour",
    close: "Close",
    nudge: {
      title: "Your first summary is ready",
      none: "No leads kept this time. Check the targeting the agent inferred from your website here: the next pass will start from your version.",
      leads: (count: number) =>
        count === 1
          ? "1 new lead, with its signal and sources. Open it here, then move it forward or dismiss it with a reason."
          : `${count} new leads, each with its signal and sources. Open them here, move the ones that matter forward and dismiss the others with a reason.`,
    },
  },
  errors: {
    signIn: "Please log in.",
  },
};
